/**
 * 기사 작성 — 업체명·대표자·질문·업종으로 보도자료형 기사 초안을 만든다.
 *
 * 전부 **운영자 전용**이다. 초안은 운영자가 검토·수정한 뒤 내보내는 내부 산출물이라
 * 고객·대행사 응답에 싣지 않는다 (260915-access-roles).
 */
import { z } from 'zod';
import { BrandKindQuery, IsoDateTime, KstDate, Ok } from '../common.ts';
import { defineRoute } from '../route.ts';

const IdParams = z.object({ id: z.uuid() });

/** 화면이 고르는 업종 — 목록 밖은 직접 입력한다(자유 문자열이다) */
export const ARTICLE_INDUSTRY_PRESETS = [
  '치과',
  '한의원',
  '피부과',
  '성형외과',
  '정형외과',
  '병의원',
  '법무법인',
] as const;

/**
 * 업체명 → 업종 (2026-10-07). 운영자가 업종을 고르지 않게 화면이 이것으로 채운다. 못 정하면 null 이고,
 * 그때만 화면이 직접 고르게 한다 — 엉뚱한 기본값(예전의 '치과')이 조용히 들어가지 않게.
 * ★ 업종은 기사에 실제로 쓰인다: 필자 직함(원장·변호사·대표), 작성 지시문, 의료기관 사진 규칙(`isMedicalIndustry`).
 * ★ **구체적인 것부터** 본다 — `강남성형외과의원` 은 '의원' 이 아니라 '성형외과', `소아치과` 는 '치과' 다.
 */
export function inferIndustry(companyName: string): string | null {
  const name = companyName.replace(/\s+/g, '');
  for (const [re, industry] of INDUSTRY_RULES) {
    const m = re.exec(name);
    if (m) return industry ?? m[0];
  }
  return null;
}
/** [이름 조각, 업종] — 업종이 null 이면 맞은 조각 그대로(진료과 이름)를 쓴다 */
const INDUSTRY_RULES: [RegExp, string | null][] = [
  [/치과/, '치과'],
  [/한의원|한방병원/, '한의원'],
  [/피부과/, '피부과'],
  [/성형외과/, '성형외과'],
  [/정형외과/, '정형외과'],
  [/법무법인|법률사무소|변호사/, '법무법인'],
  [
    /신경외과|이비인후과|산부인과|소아청소년과|소아과|비뇨의학과|비뇨기과|정신건강의학과|재활의학과|가정의학과|통증의학과|영상의학과|안과|내과|외과/,
    null,
  ],
  [/의원|병원|클리닉|메디컬/, '병의원'],
];

/** 생성 입력 — 고객을 고르면 화면이 채우고, 운영자가 손으로 고칠 수 있다 */
export const ArticleInput = z.object({
  /** 등록된 고객에서 출발했을 때만. 등록 안 된 업체도 쓸 수 있다 */
  brandId: z.uuid().nullable(),
  companyName: z.string().trim().min(1).max(120),
  representativeName: z.string().trim().min(1).max(60),
  /** 이 기사가 답하려는 질문 — 보통 그 고객의 목표 질문 하나 */
  query: z.string().trim().min(2).max(300),
  industry: z.string().trim().min(1).max(40),
});
export type ArticleInput = z.infer<typeof ArticleInput>;

/** 본문 — 문단은 빈 줄로, 소제목은 `## ` 로 시작하는 줄이다 */
export const ArticleContent = z.object({
  title: z.string().trim().min(1).max(200),
  subtitle: z.string().trim().max(300),
  body: z.string().trim().min(1).max(20000),
});
export type ArticleContent = z.infer<typeof ArticleContent>;

/**
 * draft(운영자 작성 중) → sent(광고주 검토 중) → approved(광고주 승인, 고정) → published(언론사 게재 완료).
 * 광고주는 sent·approved·published 만 본다. published 는 게재 확인(하루 한 번 11시 · 운영자 버튼)이 넘긴다.
 */
export const ARTICLE_STATUSES = ['draft', 'sent', 'approved', 'published'] as const;
export type ArticleStatus = (typeof ARTICLE_STATUSES)[number];

/** 사진 한 장 — 파일은 `/api/articles/{id}/photos/{photoId}` 로 받는다(바이너리라 계약 밖) */
export const ArticlePhoto = z.object({
  id: z.uuid(),
  fileName: z.string(),
  contentType: z.string(),
  byteSize: z.number().int(),
  createdAt: IsoDateTime,
  /**
   * 어디서 온 사진인가 — 검토 요청을 보낼 때 사진이 없으면 미리 붙인다.
   * `default` 는 고객의 기사 기본 사진, `previous` 는 같은 고객의 지난 승인 기사 사진, `uploaded` 는 이 기사에 올린 사진이다
   */
  origin: z.enum(['uploaded', 'previous', 'default']),
});
export type ArticlePhoto = z.infer<typeof ArticlePhoto>;

/** 고객의 기사 기본 사진 — 파일은 `/api/brands/{id}/article-photos/{photoId}` 로 받는다(운영자 전용, 바이너리라 계약 밖) */
export const BrandArticlePhoto = z.object({
  id: z.uuid(),
  fileName: z.string(),
  contentType: z.string(),
  byteSize: z.number().int(),
  createdAt: IsoDateTime,
});
export type BrandArticlePhoto = z.infer<typeof BrandArticlePhoto>;

/** 광고주가 올릴 수 있는 사진 — 형식·크기·장수 */
export const ARTICLE_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const ARTICLE_PHOTO_MAX_BYTES = 5 * 1024 * 1024;
export const ARTICLE_PHOTO_MAX_COUNT = 10;

/**
 * ★ 기사는 **사진이 필수**다 — 사진이 1장도 없으면 광고주가 승인할 수 없다(서버가 막는다, 2026-10-07).
 *   예전에는 칼럼 형식(제목에 "칼럼")만 필수였다.
 */
export const ARTICLE_PHOTO_MIN_COUNT = 1;

/**
 * 의료기관 업종인가 — 의료기관 기사는 **올릴 수 있는 사진이 두 종류뿐**이다(대행사 기준).
 * ★ 업종은 자유 입력이라(`ARTICLE_INDUSTRY_PRESETS` 밖은 직접 쓴다) 이름의 조각으로 가린다.
 *   사진 내용은 기계가 가리지 못하므로 화면이 안내하고, 올릴 때 한 번 더 확인받는다.
 */
export const isMedicalIndustry = (industry: string): boolean => MEDICAL_INDUSTRY_RE.test(industry);
const MEDICAL_INDUSTRY_RE =
  /치과|한의원|한방|의원|병원|클리닉|의학과|외과|내과|피부과|안과|이비인후과|산부인과|소아|비뇨|정신건강|재활|통증|의료/;

/** 의료기관 기사에 올릴 수 있는 사진 — 이 두 가지 외에는 언론사가 받지 않는다 */
export const MEDICAL_ARTICLE_PHOTOS = ['대표원장님 프로필 사진', '환자에게 설명하는 제스처가 있는 사진'] as const;

/** 언론사 게재 한 건 — 배포 대행사 보도신청목록에서 `노출완료` 를 읽어 붙인다 */
export const ArticlePublication = z.object({
  id: z.uuid(),
  /** 운영자가 손으로 넣은 게재인가 — mediap 에서 못 가져온 것. 손으로 넣은 것만 지울 수 있다 */
  manual: z.boolean(),
  outlet: z.string(),
  title: z.string(),
  url: z.string(),
  /** 대행사가 적은 게재(예약)일 `YYYY-MM-DD`. 모르면 null */
  scheduledOn: z.string().nullable(),
});
export type ArticlePublication = z.infer<typeof ArticlePublication>;

/** 배포 대행사(mediap) 신청 하나의 진행 — 게재 확인이 읽은 그대로 */
export const ArticlePressOrder = z.object({
  outlet: z.string(),
  /** 대행사 화면의 진행상태 원문 — 기사접수·언론사전달·노출완료 … */
  status: z.string(),
  /** 예약(게재)일 `YYYY-MM-DD`. 모르면 null */
  scheduledOn: z.string().nullable(),
});
export type ArticlePressOrder = z.infer<typeof ArticlePressOrder>;

/** 광고주 알림 한 건 — 기사를 보낼 때 원장님께 간 알림톡의 결과 (261010-article-alimtalk) */
export const ArticleNotice = z.object({
  at: IsoDateTime,
  /** 가린 번호(010-****-1234). 번호가 없어 못 보냈으면 null */
  recipient: z.string().nullable(),
  status: z.enum(['sent', 'failed', 'skipped']),
  /** 보냄이면 공급자 요청 ID, 실패·건너뜀이면 이유 */
  detail: z.string().nullable(),
});
export type ArticleNotice = z.infer<typeof ArticleNotice>;

export const Article = ArticleInput.extend(ArticleContent.shape).extend({
  id: z.uuid(),
  brandName: z.string().nullable(),
  /** 실제로 쓴 모델. 가짜 작성기면 `fake` */
  model: z.string(),
  status: z.enum(ARTICLE_STATUSES),
  /** 광고주에게 보낸 원고 — 광고주 수정 내역의 비교 기준. 보낸 적 없으면 null */
  sentTitle: z.string().nullable(),
  sentSubtitle: z.string().nullable(),
  sentBody: z.string().nullable(),
  sentAt: IsoDateTime.nullable(),
  /** 광고주가 마지막으로 고친 시각 — null 이면 고치지 않았다 */
  reviewerEditedAt: IsoDateTime.nullable(),
  approvedAt: IsoDateTime.nullable(),
  /** 첫 게재가 확인된 시각 */
  publishedAt: IsoDateTime.nullable(),
  photos: z.array(ArticlePhoto),
  publications: z.array(ArticlePublication),
  /** 대행사 신청 진행 — 운영자 화면 전용(광고주 응답에는 없다). 신청 전이면 빈 배열 */
  pressOrders: z.array(ArticlePressOrder),
  /** 광고주 알림(알림톡) 기록 — 운영자 화면 전용. 최근 순 */
  notices: z.array(ArticleNotice),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type Article = z.infer<typeof Article>;

export const ArticleSummary = Article.omit({
  body: true,
  sentTitle: true,
  sentSubtitle: true,
  sentBody: true,
  photos: true,
  publications: true,
  pressOrders: true,
  notices: true,
}).extend({
  photoCount: z.number().int(),
  publicationCount: z.number().int(),
  /** 가장 최근에 갱신된 대행사 신청의 진행상태(기사접수 등). 신청 전이면 null */
  pressStatus: z.string().nullable(),
});
export type ArticleSummary = z.infer<typeof ArticleSummary>;

export const listArticles = defineRoute({
  method: 'get',
  path: '/api/articles',
  access: 'admin',
  summary: '작성한 기사 목록 (최신순)',
  /** 실제 탭은 고객 없는 기사도 함께 보인다 — 테스트 탭은 테스트 브랜드의 기사만 */
  query: BrandKindQuery,
  response: z.object({ articles: z.array(ArticleSummary) }),
});

export const getArticle = defineRoute({
  method: 'get',
  path: '/api/articles/{id}',
  access: 'admin',
  summary: '기사 한 건',
  params: IdParams,
  response: z.object({ article: Article }),
});

/**
 * 고객을 골랐을 때 화면이 채울 값. 대표자명은 자가 가입 계정에만 있어 없을 수 있다.
 * 업종은 저장된 곳이 없다 — 화면이 고른다.
 */
export const articleSource = defineRoute({
  method: 'get',
  path: '/api/articles/source',
  access: 'admin',
  summary: '고객 하나의 업체명·대표자명·목표 질문',
  query: z.object({ brandId: z.uuid() }),
  response: z.object({
    companyName: z.string(),
    representativeName: z.string().nullable(),
    queries: z.array(z.string()),
  }),
});

/** 생성은 동기다 — 모델 호출이 수십 초 걸린다. 화면이 기다린다 */
export const generateArticle = defineRoute({
  method: 'post',
  path: '/api/articles',
  access: 'admin',
  summary: '기사 초안 생성',
  body: ArticleInput,
  response: z.object({ article: Article }),
});

export const updateArticle = defineRoute({
  method: 'patch',
  path: '/api/articles/{id}',
  access: 'admin',
  summary: '기사 제목·부제·본문 수정',
  params: IdParams,
  body: ArticleContent,
  response: z.object({ article: Article }),
});

export const deleteArticle = defineRoute({
  method: 'delete',
  path: '/api/articles/{id}',
  access: 'admin',
  summary: '기사 삭제',
  params: IdParams,
  response: Ok,
});

/** 광고주에게 검토를 요청한다 — 고객이 있어야 하고(brandId), 작성 중(draft)일 때만 */
export const sendArticle = defineRoute({
  method: 'post',
  path: '/api/articles/{id}/send',
  access: 'admin',
  summary: '광고주에게 검토 요청',
  params: IdParams,
  response: z.object({ article: Article }),
});

/** 광고주 알림톡을 다시 보낸다 — 검토 중(sent)인 기사만. 결과는 기사의 notices 에 남는다 */
export const notifyArticle = defineRoute({
  method: 'post',
  path: '/api/articles/{id}/notify',
  access: 'admin',
  summary: '광고주 알림톡 다시 보내기',
  params: IdParams,
  response: z.object({ article: Article }),
});

/** 검토 중인 기사를 작성 중으로 되돌린다 — 광고주가 고친 내용은 원고에 남는다. 승인된 기사는 되돌릴 수 없다 */
export const recallArticle = defineRoute({
  method: 'post',
  path: '/api/articles/{id}/recall',
  access: 'admin',
  summary: '검토 요청 회수',
  params: IdParams,
  response: z.object({ article: Article }),
});

/**
 * 게재를 손으로 넣는다 — mediap 에서 못 가져온 게재(대행사 밖에서 실린 기사·목록에서 빠진 신청).
 * ★ 광고주에게 보낸 기사(검토 중·승인·게재)에 넣는다 — 승인 전에 배포가 나가기도 한다. 작성 중은 안 된다.
 *   넣으면 기사가 published 가 되고 광고주 화면에도 보인다.
 * ★ 같은 기사에 같은 주소는 한 번만 붙는다 — 나중에 mediap 이 같은 주소를 가져와도 두 번 붙지 않는다.
 */
export const ManualPublicationInput = z.object({
  outlet: z.string().trim().min(1).max(60),
  /** 실린 기사 제목 — 비우면 우리 기사 제목을 쓴다 */
  title: z.string().trim().max(300),
  url: z
    .string()
    .trim()
    .max(2000)
    .regex(/^https?:\/\/\S+$/, '기사 주소는 http:// 또는 https:// 로 시작해야 합니다'),
  /** 게재일 `YYYY-MM-DD`. 모르면 null */
  scheduledOn: z.iso.date().nullable(),
});
export type ManualPublicationInput = z.infer<typeof ManualPublicationInput>;

export const addArticlePublication = defineRoute({
  method: 'post',
  path: '/api/articles/{id}/publications',
  access: 'admin',
  summary: '언론사 게재 직접 입력',
  params: IdParams,
  body: ManualPublicationInput,
  response: z.object({ article: Article }),
});

/** 손으로 넣은 게재를 지운다 — 마지막 게재를 지우면 기사는 승인됨(approved)으로 돌아간다 */
export const deleteArticlePublication = defineRoute({
  method: 'delete',
  path: '/api/articles/{id}/publications',
  access: 'admin',
  summary: '직접 입력한 게재 지우기',
  params: IdParams,
  query: z.object({ publicationId: z.uuid() }),
  response: z.object({ article: Article }),
});

// ── 이번 회차 기사 (운영자) ─────────────────────────────────────────────────
// ★ 기사는 **회차마다 고객 한 곳에 한 편**이다(261007-monthly-article-board). 회차는 **고객마다** 구독 개시일
//   기준 한 달이다(업체마다 시작일이 다르다). 구독은 결제 구독 하나다(261015-subscription-unify) — 구독을 넣고 고치는
//   곳은 브랜드 상세의 구독 칸이고 이 보드는 읽기만 한다. 대상은 지금 구독 중인 활성 고객이고, 운영자가 그 회차만 뺄 수 있다.
//   구독이 없거나·시작 전이거나·끝난 고객은 대상이 아니다(따로 보인다).
// ★ 회차 경계는 그 브랜드의 **구독일**이다 — 보고서 회차와 같은 날이고, 마감은 그 회차의 끝이다(행마다 `deadline`).

/** 일괄 작성 작업 상태 — 끝난(done) 작업은 기사로 보이므로 싣지 않는다 */
export const ARTICLE_JOB_STATUSES = ['queued', 'running', 'failed'] as const;

/** 구독으로 본 이 고객의 지금 — active 만 이번 회차 대상이다 */
export const SUBSCRIPTION_STATES = ['active', 'unset', 'notStarted', 'ended'] as const;

export const ArticleBoardRow = z.object({
  brandId: z.uuid(),
  brandName: z.string(),
  /** 회차 기준 구독의 개시일·마지막 이용일(KST). 없으면 null(구독 없음 · 제안만 있음) */
  subscription: z.object({ startDate: KstDate, endDate: KstDate.nullable() }).nullable(),
  /** 이 고객의 이번 회차 — active 일 때만 회차·기간·남은 날이 있다 */
  period: z.object({
    state: z.enum(SUBSCRIPTION_STATES),
    index: z.number().int().nullable(),
    from: KstDate.nullable(),
    to: KstDate.nullable(),
    /** 회차 마지막 날까지 남은 날 — 0 이면 오늘이 마감 */
    daysLeft: z.number().int().nullable(),
  }),
  /**
   * 기사 작성 마감 — **이 브랜드의** 보고서 회차의 마지막 날(구독일 기준, 261015-subscription-unify).
   * 기사가 그 회차 산출물에 잡혀야 해서다(운영자 결정 2026-10-12). 활성 고객이면 `period` 와 같은 회차다
   */
  deadline: z.object({ label: z.string(), from: KstDate, to: KstDate, daysLeft: z.number().int() }),
  /** 이번 회차에 만든 기사(가장 최근 것). 없으면 null. pressStatus 는 대행사 진행(기사접수 …) — 목록과 같은 배지를 쓴다 */
  article: z
    .object({
      id: z.uuid(),
      title: z.string(),
      status: z.enum(ARTICLE_STATUSES),
      pressStatus: z.string().nullable(),
    })
    .nullable(),
  /** 일괄 작성 작업 — 기다리는 중·쓰는 중·실패만. 없으면 null */
  job: z.object({ status: z.enum(ARTICLE_JOB_STATUSES), error: z.string().nullable() }).nullable(),
  /** 운영자가 이번 회차에서 뺐다 */
  skipped: z.boolean(),
  /** 일괄 작성에 들어갈 값 — 하나라도 null 이면 일괄로 못 쓴다(화면이 이유를 보인다) */
  representativeName: z.string().nullable(),
  /** 업체명에서 정한 업종(inferIndustry) */
  industry: z.string().nullable(),
  /** 이번에 쓸 질문 — 목표 질문 중 가장 오래 안 쓴 것 */
  nextQuery: z.string().nullable(),
});
export type ArticleBoardRow = z.infer<typeof ArticleBoardRow>;

/** 일괄로 못 쓰는 이유 — 없으면 null. 화면(체크 막기·이유 표시)과 서버(강제)가 같은 판단을 쓴다 */
export function boardRefusal(row: ArticleBoardRow): string | null {
  if (row.period.state === 'unset') return '구독이 없습니다 — 브랜드 구독 칸에서 등록해 주세요';
  if (row.period.state === 'notStarted') return '구독 시작 전입니다';
  if (row.period.state === 'ended') return '구독이 끝났습니다';
  if (row.skipped) return '이번 회차에서 뺀 고객입니다';
  if (row.article) return '이번 회차 기사가 이미 있습니다';
  if (row.job && row.job.status !== 'failed') return '이미 작성 대기 중입니다';
  if (!row.representativeName) return '대표자명이 없습니다 — 고객 계정에 이름을 넣어 주세요';
  if (!row.industry) return '업체명으로 업종을 알 수 없습니다 — 직접 작성해 주세요';
  if (!row.nextQuery) return '목표 질문이 없습니다';
  return null;
}

export const ArticleBoard = z.object({
  /** 오늘(KST) — 남은 날의 기준 */
  today: KstDate,
  /** 기사 생성 키가 있나 — 없으면 일괄 작성도 못 한다 */
  writerReady: z.boolean(),
  /** 구독 중인 고객이 구독 회차 끝이 가까운 순으로 먼저, 그다음 대상이 아닌 고객 */
  rows: z.array(ArticleBoardRow),
});
export type ArticleBoard = z.infer<typeof ArticleBoard>;

export const getArticleBoard = defineRoute({
  method: 'get',
  path: '/api/articles/board',
  access: 'admin',
  summary: '이번 회차 기사 — 고객마다 썼는지',
  query: BrandKindQuery,
  response: z.object({ board: ArticleBoard }),
});

/** 일괄 작성 대기열에 넣는다. 못 넣는 고객은 이유와 함께 돌려준다(이미 썼다·뺐다·대표자명 없음 등) */
export const queueArticles = defineRoute({
  method: 'post',
  path: '/api/articles/board/queue',
  access: 'admin',
  summary: '기사 일괄 작성 요청',
  body: z.object({ brandIds: z.array(z.uuid()).min(1).max(300) }),
  response: z.object({
    board: ArticleBoard,
    queued: z.number().int(),
    refused: z.array(z.object({ brandId: z.uuid(), reason: z.string() })),
  }),
});

/** 기다리는 작업을 모두 거둔다 — 쓰는 중인 것은 끝까지 쓴다 */
export const cancelArticleQueue = defineRoute({
  method: 'delete',
  path: '/api/articles/board/queue',
  access: 'admin',
  summary: '기사 일괄 작성 대기 취소',
  response: z.object({ board: ArticleBoard }),
});

/**
 * 대기열에서 **하나** 쓴다 — 기사 화면이 열려 있는 동안 화면이 되풀이해 부른다(1~2분 걸린다).
 * 창을 닫으면 전용 스케줄러(`/internal/scheduler/article-batch`)가 같은 대기열을 이어서 쓴다.
 */
export const runArticleQueue = defineRoute({
  method: 'post',
  path: '/api/articles/board/run',
  access: 'admin',
  summary: '기사 일괄 작성 — 다음 하나',
  response: z.object({
    /** 하나를 썼거나 실패로 끝냈으면 true. 집을 것이 없었으면 false */
    ran: z.boolean(),
    /** 아직 남은 작업(기다리는 중 + 쓰는 중) */
    remaining: z.number().int(),
  }),
});

/** 이번 회차에서 빼거나 되돌린다 */
export const skipArticleCycle = defineRoute({
  method: 'put',
  path: '/api/articles/board/skip',
  access: 'admin',
  summary: '이번 회차 기사에서 빼기·되돌리기',
  body: z.object({ brandId: z.uuid(), skipped: z.boolean() }),
  response: z.object({ board: ArticleBoard }),
});

/** 마지막 게재 확인 — 언제 했고 무엇을 붙였나. 한 번도 안 했으면 null */
export const PressSyncStatus = z.object({
  at: IsoDateTime,
  ok: z.boolean(),
  manual: z.boolean(),
  /** 성공이면 읽은 신청·새로 붙인 게재 수, 실패면 오류 */
  orders: z.number().int().nullable(),
  attached: z.number().int().nullable(),
  error: z.string().nullable(),
});
export type PressSyncStatus = z.infer<typeof PressSyncStatus>;

export const getPressSync = defineRoute({
  method: 'get',
  path: '/api/articles/press-sync',
  access: 'admin',
  summary: '마지막 게재 확인',
  response: z.object({ configured: z.boolean(), last: PressSyncStatus.nullable() }),
});

/** 지금 게재 확인 — 하루 한 번(11시) 도는 것을 기다리지 않는다. 계정이 없으면 503 */
export const runPressSync = defineRoute({
  method: 'post',
  path: '/api/articles/press-sync',
  access: 'admin',
  summary: '언론사 게재 지금 확인',
  response: z.object({ last: PressSyncStatus }),
});

// ── 광고주 검토 (고객 영역 /app) ─────────────────────────────────────────
// ★ 범위는 actor.brandIds 다(scoped). 운영자 정보(모델·작성자·보낸 원고)는 싣지 않는다 — 계약 스키마가 허용 목록이다.

export const ReviewArticle = z.object({
  id: z.uuid(),
  companyName: z.string(),
  query: z.string(),
  title: z.string(),
  subtitle: z.string(),
  body: z.string(),
  status: z.enum(['sent', 'approved', 'published']),
  /** 의료기관 기사인가 — 사진을 두 종류(`MEDICAL_ARTICLE_PHOTOS`)로만 받는다. 업종 원문은 싣지 않는다 */
  medical: z.boolean(),
  sentAt: IsoDateTime,
  reviewerEditedAt: IsoDateTime.nullable(),
  approvedAt: IsoDateTime.nullable(),
  publishedAt: IsoDateTime.nullable(),
  photos: z.array(ArticlePhoto),
  publications: z.array(ArticlePublication),
});
export type ReviewArticle = z.infer<typeof ReviewArticle>;

export const ReviewArticleSummary = ReviewArticle.omit({
  body: true,
  subtitle: true,
  photos: true,
  publications: true,
}).extend({
  photoCount: z.number().int(),
  publicationCount: z.number().int(),
});
export type ReviewArticleSummary = z.infer<typeof ReviewArticleSummary>;

export const listReviewArticles = defineRoute({
  method: 'get',
  path: '/api/review/articles',
  access: 'scoped',
  summary: '검토할 기사 목록 (내 범위, 최신순)',
  response: z.object({ articles: z.array(ReviewArticleSummary) }),
});

export const getReviewArticle = defineRoute({
  method: 'get',
  path: '/api/review/articles/{id}',
  access: 'scoped',
  summary: '검토할 기사 한 건',
  params: IdParams,
  response: z.object({ article: ReviewArticle }),
});

/** 광고주 수정 — 검토 중(sent)일 때만. 고친 내용은 운영자 화면에 보낸 원고와 대조돼 표시된다 */
export const editReviewArticle = defineRoute({
  method: 'patch',
  path: '/api/review/articles/{id}',
  access: 'scoped',
  summary: '검토 중인 기사 수정',
  params: IdParams,
  body: ArticleContent,
  response: z.object({ article: ReviewArticle }),
});

/** 승인 — 이후로는 광고주도 운영자도 고칠 수 없다. 사진이 없으면 사진 없이 발행된다 */
export const approveReviewArticle = defineRoute({
  method: 'post',
  path: '/api/review/articles/{id}/approve',
  access: 'scoped',
  summary: '기사 승인',
  params: IdParams,
  response: z.object({ article: ReviewArticle }),
});

/** 사진 올리기 — JSON 에 base64 로 싣는다(이 경로만 본문 상한이 크다, app.ts) */
export const uploadReviewPhoto = defineRoute({
  method: 'post',
  path: '/api/review/articles/{id}/photos',
  access: 'scoped',
  summary: '기사 사진 올리기',
  params: IdParams,
  body: z.object({
    fileName: z.string().trim().min(1).max(200),
    contentType: z.enum(ARTICLE_PHOTO_TYPES),
    dataBase64: z
      .string()
      .min(1)
      .max(Math.ceil((ARTICLE_PHOTO_MAX_BYTES * 4) / 3) + 8),
  }),
  response: z.object({ article: ReviewArticle }),
});

export const deleteReviewPhoto = defineRoute({
  method: 'delete',
  path: '/api/review/articles/{id}/photos',
  access: 'scoped',
  summary: '기사 사진 지우기',
  params: IdParams,
  query: z.object({ photoId: z.uuid() }),
  response: z.object({ article: ReviewArticle }),
});

// ── 고객의 기사 기본 사진(운영자) ─────────────────────────────────────────

/**
 * 고객의 기사 기본 사진 — 기사를 광고주에게 보낼 때 사진이 없으면 이 사진을 먼저 붙인다(261012-client-article-photos).
 * 의료기관이면 `MEDICAL_ARTICLE_PHOTOS` 두 종류만 올린다 — 화면이 안내한다.
 */
export const listBrandArticlePhotos = defineRoute({
  method: 'get',
  path: '/api/brands/{id}/article-photos',
  access: 'admin',
  summary: '고객의 기사 기본 사진',
  params: IdParams,
  response: z.object({ photos: z.array(BrandArticlePhoto) }),
});

export const uploadBrandArticlePhoto = defineRoute({
  method: 'post',
  path: '/api/brands/{id}/article-photos',
  access: 'admin',
  summary: '고객의 기사 기본 사진 올리기',
  params: IdParams,
  body: uploadReviewPhoto.body,
  response: z.object({ photos: z.array(BrandArticlePhoto) }),
});

export const deleteBrandArticlePhoto = defineRoute({
  method: 'delete',
  path: '/api/brands/{id}/article-photos',
  access: 'admin',
  summary: '고객의 기사 기본 사진 지우기 — 이미 기사에 붙은 사진은 그대로 남는다',
  params: IdParams,
  query: z.object({ photoId: z.uuid() }),
  response: z.object({ photos: z.array(BrandArticlePhoto) }),
});
